/* eslint-disable jsx-a11y/role-supports-aria-props */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback } from "react";
import { MenuProps, components } from "react-select";
import cx from "classnames";
import { debounce } from "lodash";

import "../styles/custom-menu.scss";
import { getRequest } from "@utils/request";
import { stringify } from "qs";
import { Groups } from "@containers/Settings/Users/Groups/GroupListing/constants";
import Loader from "../Loader";
import "../suggestionCustomStyle.scss";
import { GroupOption, Option } from "./FormField";

// export interface GroupOption extends GroupBase<Option> {}

export interface CustomMenuProps
	extends MenuProps<Option, boolean, GroupOption> {
	selectProps: MenuProps<Option, boolean, GroupOption>["selectProps"] & {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		selectProps?: Record<string, any>;
	};
}

interface TabConfig {
	id: number;
	label: string;
	panelId: string;
	tabId: string;
}

const TABS: TabConfig[] = [
	{
		id: 1,
		label: "Users",
		panelId: "users-tab-panel",
		tabId: "users-tab",
	},
	{
		id: 2,
		label: "Groups",
		panelId: "groups-tab-panel",
		tabId: "groups-tab",
	},
];

const API_ENDPOINTS = {
	USERS: "/api/v2/params-lookup",
	GROUPS: "/api/v2/user-groups",
} as const;

interface MappedUsers {
	[key: string]: Option;
}

export const CustomMenu = (props: CustomMenuProps): JSX.Element => {
	const [activeTab, setActiveTab] = React.useState(
		(props.selectProps.value as Option)?.isGroup ? 2 : 1,
	);
	const [hasMore, setHasMore] = React.useState(true);
	const [options, setOptions] = React.useState<Option[]>([]);
	const [allUsers, setAllUsers] = React.useState<MappedUsers>({});
	const [loading, setLoading] = React.useState(false);
	const searchSequenceRef = React.useRef(0);
	const isInitialLoad = React.useRef(true);

	const debouncedSearch = React.useMemo(
		() =>
			debounce((searchValue: string, tab: number) => {
				setOptions([]);
				setHasMore(true);
				loadMore(tab, false, searchValue);
			}, 300),
		[],
	);

	React.useEffect(() => {
		setLoading(true);
		loadMore(activeTab, false, props.selectProps.inputValue);

		const { selectProps = {} } = props.selectProps;
		const {
			field: { fieldId },
			moduleId,
			bypass,
		} = selectProps;
		const fetchUsers = async (): Promise<void> => {
			const query: Record<string, string | number> = {};
			query.bypass = bypass;
			query.fieldId = fieldId;
			query.type = "all";

			if (fieldId === "F5") {
				query.moduleId = moduleId;
			}
			const {
				data: { data },
			} = await getRequest(`/api/v2/params-lookup?${stringify(query)}`);

			const usersMap = (data?.options || []).reduce(
				(acc: MappedUsers, user: Option) => {
					if (user.value) {
						acc[user.value] = user;
					}
					return acc;
				},
				{},
			);

			setAllUsers(usersMap);
		};

		if (fieldId === "F5") {
			fetchUsers();
		}

		return () => {
			debouncedSearch.cancel();
		};
	}, []);

	React.useEffect(() => {
		if (isInitialLoad.current) {
			isInitialLoad.current = false;
			return;
		}
		debouncedSearch(props.selectProps.inputValue, activeTab);
	}, [props.selectProps.inputValue, activeTab]);

	const loadMore = async (
		tab: number = 1,
		isLoadMore: boolean = false,
		searchValue?: string,
	): Promise<void> => {
		if (loading && isLoadMore) return;

		const currentSearchSequence = searchSequenceRef.current + 1;
		searchSequenceRef.current = currentSearchSequence;
		setLoading(true);

		try {
			const { selectProps = {} } = props.selectProps;
			const endpoint = tab === 2 ? API_ENDPOINTS.GROUPS : API_ENDPOINTS.USERS;

			const query: Record<string, string | number> = {
				search: searchValue ?? props.selectProps.inputValue ?? "",
			};

			if (tab === 1) {
				const {
					field: { fieldId },
					moduleId,
					bypass,
				} = selectProps;
				query.bypass = bypass;
				query.fieldId = fieldId;

				if (fieldId === "F5") {
					query.moduleId = moduleId;
				}
				query.skip = isLoadMore ? options.length : 0;
			} else if (tab === 2) {
				query.$skip = isLoadMore ? options.length : 0;
				if (selectProps.selectedGroupId) {
					query.excludeId = selectProps.selectedGroupId;
				}
			}

			const {
				data: { data },
			} = await getRequest(`${endpoint}?${stringify(query)}`);

			if (currentSearchSequence === searchSequenceRef.current) {
				let oldData: Option[] = [];
				if (isLoadMore) oldData = [...options];

				let newOptions = tab === 2 ? data?.data || [] : data?.options || [];
				const count = newOptions.length + (oldData?.length || 0);

				if (selectProps.isGroup && tab === 2) {
					newOptions = newOptions
						.filter((each: Groups) => each?.users?.length)
						.map((item: Groups) => ({
							label: item.name,
							value: item.id || item._id,
							description: item.description,
							isGroup: true,
							users: item?.users,
						}));
				}

				setOptions([...oldData, ...newOptions]);
				setHasMore(count < (data.total || data.totalRecords));
			}
		} catch (error) {
			//
		} finally {
			if (currentSearchSequence === searchSequenceRef.current) {
				setLoading(false);
			}
		}
	};

	const handleTabClick = useCallback(
		(e: React.MouseEvent, tabId: number): void => {
			e.preventDefault();
			e.stopPropagation();

			debouncedSearch.cancel();
			searchSequenceRef.current += 1;

			setOptions([]);
			setLoading(true);
			setActiveTab(tabId);
		},
		[props.selectProps.inputValue],
	);

	const isItemSelected = (item: Option): boolean => {
		const selectedValue = props.selectProps.value as Option;
		return selectedValue?.value === item.value;
	};

	const filteredOptions = React.useMemo(() => {
		const isGroup = props?.selectProps?.selectProps?.isGroup;

		if (isGroup && activeTab === 2) {
			return options.filter(
				each =>
					each.isGroup &&
					Array.isArray(each.users) &&
					each.users.some(userId => allUsers[userId]),
			);
		}

		return options;
	}, [options, activeTab, allUsers, props?.selectProps?.selectProps?.isGroup]);

	return (
		<components.Menu {...props}>
			<div
				className="pc-custom-menu-container"
				onMouseDown={(e: React.MouseEvent<HTMLDivElement>): void =>
					e.stopPropagation()
				}
			>
				<div className="pc-tabs-container">
					{TABS.map(tab => (
						<button
							key={tab.id}
							type="button"
							className={cx("pc-tab-button", {
								active: activeTab === tab.id,
							})}
							id={tab.tabId}
							aria-controls={tab.panelId}
							aria-selected={activeTab === tab.id}
							onMouseDown={(e: React.MouseEvent<HTMLButtonElement>): void =>
								handleTabClick(e, tab.id)
							}
						>
							{tab.label}
						</button>
					))}
				</div>
				<div
					className={`${
						filteredOptions?.length < 1 && !loading ? "emptyList" : ""
					} pc-options-container`}
					onScroll={(e: React.UIEvent<HTMLDivElement>): void => {
						const target = e.target as HTMLDivElement;
						if (
							target.scrollHeight - target.scrollTop <=
								target.clientHeight + 50 &&
							hasMore &&
							!loading
						) {
							loadMore(activeTab, true);
						}
					}}
				>
					{filteredOptions?.length > 0 ? (
						filteredOptions.map(item => (
							<div
								key={item.value}
								className={cx("pc-option-item", {
									"pc-option-item--selected": isItemSelected(item),
								})}
								onMouseDown={(e: React.MouseEvent<HTMLDivElement>): void => {
									e.preventDefault();
									e.stopPropagation();
									props.selectOption(item);
								}}
							>
								<p className="option-label">{item.label}</p>
								{props?.selectProps?.selectProps?.isGroup &&
									activeTab === 2 && (
										<p className="option-description">
											{(item.users || [])
												.map((userId: string) => allUsers[userId]?.label)
												.filter(Boolean)
												.join(", ")}
										</p>
									)}
							</div>
						))
					) : filteredOptions?.length === 0 && !loading ? (
						<div className="noOptionsContainer">
							<p>No Options</p>
						</div>
					) : (
						<div className="pc-loading-indicator">
							<Loader />
						</div>
					)}
					{filteredOptions?.length > 0 && loading && (
						<div className="loading">Loading...</div>
					)}
				</div>
			</div>
		</components.Menu>
	);
};

export default CustomMenu;
